/**
 * CompanyFiscalSection — seção "Dados fiscais (NFS-e)" do modal da empresa.
 *
 * A chave mestra é o REGIME: Autônomo → sem NFS-e (nada mais a preencher);
 * MEI/ME-EPP → mostra município + certificado, e o CNPJ (campo do modal acima)
 * vira obrigatório. Substitui o antigo FiscalOnboardingWizard. Componente
 * auto-contido: carrega o fiscal_profile da empresa, resolve o município inicial,
 * e reporta {taxpayerType, muni} ao modal via onChange (o modal persiste no save).
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { MunicipioCombobox } from '@/components/nfse/MunicipioCombobox';
import { CertificateUploader } from '@/components/nfse/CertificateUploader';
import { AlertTriangle, Info, MapPin } from 'lucide-react';
import {
  loadTodosMunicipios,
  loadMunicipiosNacionais,
  findMunicipioByIbge,
  suggestMunicipioFromText,
  type MunicipioNacional,
} from '@/lib/nfseMunicipios';
import type { TaxpayerType } from '@/types/fiscal';

export interface FiscalSectionValue {
  taxpayerType: TaxpayerType;
  muni: MunicipioNacional | null;
  /** true só APÓS hidratar do perfil salvo. Enquanto false, o modal NÃO deve
   *  persistir/validar — senão salvar durante o load sobrescreve dados do banco. */
  ready: boolean;
}

// ME/EPP: backend suporta, mas só MEI foi provado ao vivo → "Em breve" (desabilitado).
const REGIMES: Array<{ value: TaxpayerType; label: string; hint: string; soon?: boolean }> = [
  { value: 'autonomo_informal', label: 'Autônomo (sem CNPJ)', hint: 'Não emite NFS-e. Você pode usar Recibos.' },
  { value: 'mei', label: 'MEI', hint: 'Microempreendedor Individual. Emite NFS-e pelo Padrão Nacional.' },
  { value: 'me_epp', label: 'ME / EPP', hint: 'Em breve.', soon: true },
];

export function CompanyFiscalSection({
  companyId,
  isOwner,
  cnpjDigits,
  pixCity,
  address,
  onChange,
}: {
  companyId: string | null;
  isOwner: boolean;
  cnpjDigits: string;
  /** Cidade do PIX / endereço do cadastro — usados p/ pré-sugerir o município. */
  pixCity?: string;
  address?: string;
  onChange: (value: FiscalSectionValue) => void;
}) {
  const [taxpayerType, setTaxpayerType] = useState<TaxpayerType>('autonomo_informal');
  const [muni, setMuni] = useState<MunicipioNacional | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [suggestion, setSuggestion] = useState<MunicipioNacional | null>(null);

  // Carrega o perfil fiscal da empresa em edição (se houver).
  const { data: profile } = useQuery({
    queryKey: ['fiscal-profile', companyId],
    enabled: Boolean(companyId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('fiscal_profiles').select('*').eq('company_id', companyId as string).maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
  });

  // Hidrata regime + município a partir do perfil, uma vez.
  useEffect(() => {
    if (hydrated) return;
    if (!companyId) { setHydrated(true); return; } // empresa nova → defaults
    if (profile === undefined) return; // ainda carregando
    if (profile?.taxpayer_type) setTaxpayerType(profile.taxpayer_type as TaxpayerType);
    (async () => {
      if (profile?.municipio_ibge) {
        const list = await loadTodosMunicipios();
        setMuni(findMunicipioByIbge(list, profile.municipio_ibge));
      }
      setHydrated(true);
    })();
  }, [companyId, profile, hydrated]);

  // Reporta ao modal SÓ depois de hidratar (evita persistir os defaults provisórios
  // e apagar dados do perfil se o usuário salvar durante o carregamento).
  useEffect(() => {
    if (hydrated) onChange({ taxpayerType, muni, ready: true });
  }, [hydrated, taxpayerType, muni, onChange]);

  // Pré-sugere o município a partir do pix_city/address quando ainda não há um
  // escolhido (setup inicial). Não seleciona sozinho — mostra um chip a confirmar.
  // A regra de match (pura) vive em suggestMunicipioFromText; aqui só orquestra.
  useEffect(() => {
    const emitter = taxpayerType === 'mei' || taxpayerType === 'me_epp';
    const hasText = Boolean((pixCity ?? '').trim() || (address ?? '').trim());
    if (!hydrated || !emitter || muni || profile?.municipio_ibge || !hasText) {
      setSuggestion(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const list = taxpayerType === 'mei'
        ? await loadTodosMunicipios()
        : await loadMunicipiosNacionais();
      const s = suggestMunicipioFromText(list, pixCity, address);
      if (!cancelled) setSuggestion(s);
    })();
    return () => { cancelled = true; };
  }, [hydrated, taxpayerType, muni, profile?.municipio_ibge, pixCity, address]);

  const isEmitter = taxpayerType === 'mei' || taxpayerType === 'me_epp';

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Regime tributário</Label>
        <RadioGroup
          value={taxpayerType}
          onValueChange={(v) => setTaxpayerType(v as TaxpayerType)}
          className="gap-2"
        >
          {REGIMES.map((r) => (
            <Label
              key={r.value}
              htmlFor={`reg-${r.value}`}
              className={
                'flex cursor-pointer items-start gap-3 rounded-lg border p-3 ' +
                (r.soon ? 'cursor-not-allowed opacity-60 ' : '') +
                (taxpayerType === r.value ? 'border-primary ring-1 ring-primary' : '')
              }
            >
              <RadioGroupItem id={`reg-${r.value}`} value={r.value} disabled={r.soon} className="mt-0.5" />
              <span>
                <span className="flex items-center gap-2 text-sm font-medium">
                  {r.label}
                  {r.soon && <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">Em breve</span>}
                </span>
                <span className="text-xs text-muted-foreground">{r.hint}</span>
              </span>
            </Label>
          ))}
        </RadioGroup>
      </div>

      {isEmitter && (
        <div className="space-y-4 rounded-lg border bg-muted/20 p-3">
          {cnpjDigits.length !== 14 && (
            <p className="flex items-center gap-2 rounded-md bg-amber-50 p-2 text-xs text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              Informe o <strong>CNPJ</strong> no campo acima — é obrigatório para emitir NFS-e.
            </p>
          )}

          <div className="space-y-1.5">
            <Label>Município</Label>
            <MunicipioCombobox value={muni} onChange={setMuni} mode={taxpayerType === 'mei' ? 'all' : 'adherent'} />
            {!muni && suggestion && (
              <button
                type="button"
                onClick={() => setMuni(suggestion)}
                className="mt-1.5 flex w-full items-center gap-1.5 rounded-md border border-primary/40 bg-primary/5 px-2.5 py-1.5 text-left text-xs text-primary hover:bg-primary/10"
              >
                <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span>Usar <strong>{suggestion.nome}/{suggestion.uf}</strong>? (do cadastro da empresa)</span>
              </button>
            )}
            {muni && (
              <p className="text-xs text-muted-foreground">
                {muni.provider === 'nacional'
                  ? 'Emite pela NFS-e Nacional (Sefin).'
                  : `${muni.nome} usa sistema próprio (${muni.provider === 'sao_paulo' ? 'São Paulo' : 'Fortaleza'}).`}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Certificado digital A1 <span className="font-normal text-muted-foreground">(opcional agora)</span></Label>
            <CertificateUploader companyId={companyId} isOwner={isOwner} />
          </div>

          <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
            <Info className="mt-0.5 h-3 w-3 shrink-0" />
            Nome, endereço e demais dados do prestador são preenchidos automaticamente pela Receita/Sefin a partir do CNPJ.
          </p>
        </div>
      )}
    </div>
  );
}
